import React, { useState } from "react"

const ContactSection = ({ lawyer, onGetDirections, currentUser }) => {
  const [currentOfficeIndex, setCurrentOfficeIndex] = useState(0);
  const [viewMode, setViewMode] = useState('carousel'); // 'carousel' or 'grid'

  const nextOffice = () => {
    if (lawyer.offices && lawyer.offices.length > 1) {
      setCurrentOfficeIndex((prev) => (prev + 1) % lawyer.offices.length);
    }
  };

  const prevOffice = () => {
    if (lawyer.offices && lawyer.offices.length > 1) {
      setCurrentOfficeIndex((prev) => (prev - 1 + lawyer.offices.length) % lawyer.offices.length);
    }
  };

  const goToOffice = (index) => {
    setCurrentOfficeIndex(index);
  };

  // Get primary office (first office or the one we're currently viewing)
  const primaryOffice = lawyer.offices?.[0];
  const currentOffice = lawyer.offices?.[currentOfficeIndex] || primaryOffice;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow h-full">
      <h3 className="text-xl font-semibold text-gray-800 mb-5 flex items-center">
        <svg
          className="w-5 h-5 mr-2 text-indigo-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          ></path>
        </svg>
        Contact Details
        {lawyer.offices && lawyer.offices.length > 1 && (
          <span className="ml-2 text-sm text-gray-500">
            ({lawyer.offices.length} offices)
          </span>
        )}
      </h3>

      {/* Map if coordinates exist */}
      {lawyer.contactDetails?.latitude && lawyer.contactDetails?.longitude && (
        <div className="mb-5">
          <div className="rounded-lg overflow-hidden h-48 mb-3">
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${lawyer.contactDetails.latitude},${lawyer.contactDetails.longitude}`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <button
            onClick={onGetDirections}
            className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors flex items-center justify-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
              ></path>
            </svg>
            Get directions
          </button>
        </div>
      )}

      {/* Multiple Offices - Carousel or Grid */}
      {lawyer.offices && lawyer.offices.length > 1 && (
        <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-500">Other Office Locations</h4>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('carousel')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  viewMode === 'carousel' 
                    ? 'bg-indigo-100 text-indigo-800' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Carousel
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-indigo-100 text-indigo-800' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Grid
              </button>
            </div>
          </div>

          {viewMode === 'carousel' ? (
            // Carousel View
            <div className="relative w-full">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentOfficeIndex * 100}%)` }}
                >
                  {lawyer.offices.map((office, index) => (
                    <div key={office.office_id || index} className="w-full flex-shrink-0 p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-gray-800">
                          {office.town || `Office ${index + 1}`}
                          {office.office_type === 'head' && (
                            <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Head
                            </span>
                          )}
                          {office.office_type === 'main' && (
                            <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Main
                            </span>
                          )}
                        </h5>
                        {office.phone_number && currentUser && (
                          <a
                            href={`tel:${office.phone_number}`}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                            title="Call this office"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </a>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        {office.name && office.name !== office.town && (
                          <p className="font-medium">{office.name}</p>
                        )}
                        {office.address1 && <p>{office.address1}</p>}
                        {office.address2 && <p>{office.address2}</p>}
                        {office.postcode && <p>{office.postcode}</p>}
                        {office.email && (
                          <p>
                            {currentUser ? (
                              <a href={`mailto:${office.email}`} className="text-blue-600 hover:text-blue-800">
                                {office.email}
                              </a>
                            ) : (
                              <span className="text-gray-500 italic">Please sign up to view contact details</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Carousel Controls */}
              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={prevOffice}
                  className="p-2 rounded-full bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  disabled={lawyer.offices.length <= 1}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Office counter */}
                <div className="text-sm text-gray-600">
                  <span className="font-medium">
                    {currentOfficeIndex + 1} of {lawyer.offices.length}
                  </span>
                </div>

                <button
                  onClick={nextOffice}
                  className="p-2 rounded-full bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  disabled={lawyer.offices.length <= 1}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            // Grid View
            <div className="grid grid-cols-1 gap-3">
              {lawyer.offices.map((office, index) => (
                <div key={office.office_id || index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-sm text-gray-800">
                      {office.town || `Office ${index + 1}`}
                      {office.office_type === 'head' && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                          Head
                        </span>
                      )}
                      {office.office_type === 'main' && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                          Main
                        </span>
                      )}
                    </h5>
                    {office.phone_number && currentUser && (
                      <a
                        href={`tel:${office.phone_number}`}
                        className="p-1 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                        title="Call this office"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </a>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-600 space-y-1">
                    {office.name && office.name !== office.town && (
                      <p className="font-medium">{office.name}</p>
                    )}
                    {office.address1 && <p>{office.address1}</p>}
                    {office.postcode && <p>{office.postcode}</p>}
                    <div className="flex items-center justify-between mt-2">
                      {office.phone_number && (
                        <span className="text-blue-600">
                          {currentUser ? office.phone_number : "Please sign up to view contact details"}
                        </span>
                      )}
                      {office.email && (
                        currentUser ? (
                          <a href={`mailto:${office.email}`} className="text-blue-600 hover:text-blue-800">
                            Email
                          </a>
                        ) : (
                          <span className="text-gray-500 italic text-xs">Please sign up to view contact details</span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Office Contact Details - Moved Down */}
      {lawyer.contactDetails && (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Main Office</h4>
            <p className="text-gray-800">{lawyer.contactDetails.mainOffice || "Not available"}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Address</h4>
            {lawyer.contactDetails.address !== "Not available" ? (
              <>
                <p className="text-gray-800">{lawyer.contactDetails.address}</p>
                <p className="text-gray-600">
                  {lawyer.contactDetails.city && lawyer.contactDetails.city !== "Not available"
                    ? lawyer.contactDetails.city
                    : ""}
                </p>
              </>
            ) : (
              <p className="text-gray-800">Not available</p>
            )}
          </div>

          {/* Phone, Email, Website - Main Office Details */}
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-gray-500 mr-3 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                ></path>
              </svg>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Phone</h4>
                <p className="text-gray-800">
                  {currentUser && lawyer.contactDetails.phone && lawyer.contactDetails.phone !== "Not available"
                    ? lawyer.contactDetails.phone
                    : !currentUser 
                      ? "Please sign up to view contact details"
                      : "Not available"}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-gray-500 mr-3 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                ></path>
              </svg>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Email</h4>
                <p className="text-gray-800">
                  {currentUser && lawyer.contactDetails.email && lawyer.contactDetails.email !== "Not available"
                    ? lawyer.contactDetails.email
                    : !currentUser 
                      ? "Please sign up to view contact details"
                      : "Not available"}
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-gray-500 mr-3 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                ></path>
              </svg>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Website</h4>
                {currentUser && lawyer.contactDetails.website && lawyer.contactDetails.website !== "Not available" ? (
                  <a
                    href={
                      lawyer.contactDetails.website.startsWith("http")
                        ? lawyer.contactDetails.website
                        : `https://${lawyer.contactDetails.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {lawyer.contactDetails.website}
                  </a>
                ) : (
                  <p className="text-gray-500 italic">
                    {!currentUser ? "Please sign up to view contact details" : "Not available"}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactSection 